import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'

import { ImagePlate } from '@/components/generals/image-plate'
import { WordReveal } from '@/components/generals/word-reveal'
import { CyclingWord } from '@/components/generals/cycling-word'
import { LocalTime } from '@/components/contact/local-time'
import { useParallax } from '@/hooks/use-parallax'

import { EASE, MICRO_FAINT } from '@/constants/type'
import { studio } from '@/constants/site'

/**
 * The first word has to stand on its own: cycling is switched off under
 * `prefers-reduced-motion`, which leaves it showing.
 */
const SUBJECTS = [
    'an identity.',
    'a typeface.',
    'a book.',
    'a signage system.',
    'a reading app.',
    'something else.'
]

export const ContactHero: FC = (): ReactNode => {
    const [ready, setReady] = useState(false)
    const drift = useParallax<HTMLDivElement>(-34)

    useEffect(() => {
        setReady(true)
    }, [])

    const rise = (delay: number): CSSProperties => ({
        opacity: ready ? 1 : 0,
        transform: ready ? 'translateY(0)' : 'translateY(10px)',
        transition: `opacity 0.7s ${EASE} ${delay}s, transform 0.7s ${EASE} ${delay}s`
    })

    return (
        <header
            id='contact-hero'
            className='px-6 pb-10 pt-28 sm:px-12 sm:pb-12 sm:pt-40'
        >
            <div className='grid grid-cols-12 items-baseline gap-x-6'>
                <p className={`col-span-6 sm:col-span-2 ${MICRO_FAINT}`} style={rise(0.05)}>
                    Contact
                </p>

                <p
                    className={`col-span-6 text-right sm:col-start-11 sm:col-span-2 sm:text-left ${MICRO_FAINT}`}
                    style={rise(0.14)}
                >
                    {studio.cities.join(' · ')}
                </p>
            </div>

            {/* Top-weighted: the type leads, the picture answers underneath. */}
            <div className='mt-12 grid grid-cols-12 items-start gap-x-6 gap-y-10 sm:mt-16'>
                <h1
                    aria-label='Tell us about your next project.'
                    className='col-span-12 font-serif tracking-[-0.03em] text-fz-ink lg:col-span-8'
                    style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', lineHeight: 1 }}
                >
                    <span aria-hidden='true' className='block'>
                        <WordReveal as='span' variant='fade' text='Tell us about' delay={0.28} />
                    </span>

                    {/* The slot's first word is opaque from the first paint, so the
                        line is faded in from out here rather than from inside it. */}
                    <span
                        aria-hidden='true'
                        className='block'
                        style={{
                            opacity: ready ? 1 : 0,
                            transition: `opacity 1.1s ${EASE} 0.52s`
                        }}
                    >
                        <CyclingWord
                            words={SUBJECTS}
                            variant='fade'
                            interval={2600}
                            className='text-fz-sub'
                        />
                    </span>
                </h1>

                <div
                    className='col-span-12 flex gap-10 lg:col-start-10 lg:col-span-3 lg:flex-col lg:gap-6'
                    style={rise(0.55)}
                >
                    <div>
                        <p className={MICRO_FAINT}>{studio.timeCity}</p>

                        <p className='mt-2 text-[15px] leading-relaxed text-fz-ink'>
                            <LocalTime />
                        </p>
                    </div>

                    <div>
                        <p className={MICRO_FAINT}>Availability</p>

                        <p className='mt-2 max-w-[22ch] text-[15px] leading-relaxed text-fz-sub'>
                            Booking from March. Two slots left this year.
                        </p>
                    </div>
                </div>
            </div>

            {/* A wide band that uncovers sideways, rather than the About plates
                that lift. Different gesture, same vocabulary. */}
            <div ref={drift} className='mt-12 sm:mt-16'>
                <ImagePlate
                    src='/images/contact-hero.jpg'
                    alt='Portrait turned toward a window'
                    ratio='16 / 7'
                    focus='50% 32%'
                    trigger='mount'
                    wipe='side'
                    priority
                    delay={0.62}
                />
            </div>

            <div className='mt-8'>
                <span
                    aria-hidden='true'
                    className='block h-px w-full origin-left bg-fz-rule'
                    style={{
                        transform: ready ? 'scaleX(1)' : 'scaleX(0)',
                        transition: `transform 1.3s ${EASE} 0.85s`
                    }}
                />

                <div className='mt-6 flex items-end justify-between gap-10'>
                    <p
                        className='max-w-[46ch] text-[15px] leading-relaxed text-fz-sub'
                        style={rise(1)}
                    >
                        There is no form to fill in, only a letter to finish. We read every one
                        ourselves, so a reply takes a day or two rather than an hour.
                    </p>

                    <div
                        className='hidden shrink-0 items-center gap-4 sm:flex'
                        style={rise(1.1)}
                        aria-hidden='true'
                    >
                        <span className={MICRO_FAINT}>Write</span>

                        <span className='relative block h-12 w-px overflow-hidden bg-fz-rule'>
                            <span className='fz-cue absolute inset-x-0 top-0 block h-4 bg-fz-rule-on' />
                        </span>
                    </div>
                </div>
            </div>
        </header>
    )
}
